import React, { useState, useEffect, useRef } from 'react';
import './SelectField.css';

const SelectField = ({
  id,
  value,
  onChange,
  options,
  disabled,
  placeholder,
  required = false,
  invalid = false,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const listboxId = `${id || 'select'}-listbox`;

  const selectedOption =
    options.find((opt) => opt.value === value) || options[0] || null;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (newValue) => {
    if (disabled) return;
    onChange(newValue);
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen((prev) => !prev);
    } else if (event.key === 'Escape') {
      setOpen(false);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const selectable = options.filter((opt) => opt.value !== '' || !required);
      const currentIndex = selectable.findIndex((opt) => opt.value === value);
      const dir = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex =
        currentIndex === -1
          ? 0
          : (currentIndex + dir + selectable.length) % selectable.length;
      const next = selectable[nextIndex];
      if (next) onChange(next.value);
    } else if (event.key === 'Home') {
      event.preventDefault();
      const first = options.find((opt) => opt.value !== '' || !required) || options[0];
      if (first) onChange(first.value);
    } else if (event.key === 'End') {
      event.preventDefault();
      const last =
        [...options].reverse().find((opt) => opt.value !== '' || !required) ||
        options[options.length - 1];
      if (last) onChange(last.value);
    }
  };

  return (
    <div
      className={`custom-select${disabled ? ' custom-select--disabled' : ''}${invalid ? ' custom-select--invalid' : ''}`}
      ref={containerRef}
    >
      <button
        type="button"
        id={id}
        className="custom-select-trigger"
        onClick={() => !disabled && setOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        disabled={disabled}
      >
        {selectedOption && selectedOption.icon && (
          <img src={selectedOption.icon} alt="" className="custom-select-icon" />
        )}
        <span className="custom-select-label">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="custom-select-caret" aria-hidden="true" />
      </button>

      {open && (
        <div className="custom-select-menu" role="listbox" id={listboxId}>
          {options.map((opt) => (
            <button
              type="button"
              key={opt.value || '__all'}
              className={`custom-select-option${
                opt.value === value ? ' custom-select-option--selected' : ''
              }`}
              onClick={() => handleSelect(opt.value)}
              role="option"
              aria-selected={opt.value === value}
            >
              {opt.icon && (
                <img src={opt.icon} alt="" className="custom-select-option-icon" />
              )}
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectField;
